import React from 'react';
import { motion } from 'framer-motion';
import { Users, Target, Eye, Award, Lightbulb, Heart } from 'lucide-react';
import Tilt from 'react-parallax-tilt';

const About: React.FC = () => {
  const stats = [
    { number: '100+', label: 'Projects Completed', gradient: 'from-blue-500 to-cyan-500' },
    { number: '50+', label: 'Happy Clients', gradient: 'from-purple-500 to-pink-500' },
    { number: '5+', label: 'Years Experience', gradient: 'from-green-500 to-emerald-500' },
    { number: '24/7', label: 'Support', gradient: 'from-orange-500 to-red-500' }
  ];

  const team = [
    {
      name: 'John Smith',
      role: 'CEO & Founder',
      description: 'Visionary leader with 15+ years in technology and business development.',
      gradient: 'from-blue-500 to-purple-500'
    },
    {
      name: 'Sarah Johnson',
      role: 'CTO',
      description: 'Technical expert specializing in AI, automation, and scalable architectures.',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      name: 'Mike Davis',
      role: 'Head of Operations',
      description: 'Operations specialist ensuring seamless project delivery and client satisfaction.',
      gradient: 'from-green-500 to-blue-500'
    },
    {
      name: 'Emily Chen',
      role: 'Lead Developer',
      description: 'Full-stack developer with expertise in modern web technologies and cloud solutions.',
      gradient: 'from-red-500 to-orange-500'
    }
  ];

  const values = [
    {
      icon: Award,
      title: 'Excellence',
      description: 'We strive for excellence in every project and interaction',
      gradient: 'from-yellow-400 to-orange-500'
    },
    {
      icon: Users,
      title: 'Collaboration',
      description: 'We believe in the power of teamwork and partnership',
      gradient: 'from-blue-500 to-purple-500'
    },
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'We continuously push boundaries and embrace new technologies',
      gradient: 'from-green-500 to-cyan-500'
    },
    {
      icon: Heart,
      title: 'Transparency',
      description: 'We maintain open and honest communication with all stakeholders',
      gradient: 'from-pink-500 to-red-500'
    }
  ];

  const timeline = [
    {
      year: '2019',
      title: 'Company Founded',
      description: 'Started with a vision to bridge traditional business and cutting-edge technology'
    },
    {
      year: '2020',
      title: 'First Major Client',
      description: 'Delivered our first enterprise-level automation solution'
    },
    {
      year: '2021',
      title: 'AI Integration',
      description: 'Expanded services to include AI-powered solutions and machine learning'
    },
    {
      year: '2022',
      title: 'Global Expansion',
      description: 'Extended operations to serve international markets and clients'
    },
    {
      year: '2023',
      title: 'Innovation Hub',
      description: 'Established dedicated R&D center for emerging technologies'
    },
    {
      year: '2024',
      title: 'Future Vision',
      description: 'Leading the next wave of digital transformation solutions'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pt-20">
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-20 right-20 w-72 h-72 bg-gradient-to-br from-primary-400/20 to-purple-600/20 dark:from-cyan-400/10 dark:to-purple-600/10 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
          />
          <motion.div
            className="absolute bottom-20 left-20 w-72 h-72 bg-gradient-to-tr from-cyan-400/20 to-blue-600/20 dark:from-cyan-400/10 dark:to-blue-600/10 rounded-full blur-3xl"
            animate={{
              scale: [1.2, 1, 1.2],
              rotate: [360, 180, 0],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1
              className="text-5xl md:text-7xl font-bold mb-8 bg-gradient-to-r from-gray-900 via-primary-600 to-purple-600 dark:from-white dark:via-cyan-400 dark:to-purple-400 bg-clip-text text-transparent"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              About Lunexa
            </motion.h1>
            <motion.p
              className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              We are a forward-thinking technology company dedicated to transforming businesses 
              through innovative IT solutions, automation, and artificial intelligence.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-8 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                Our Story
              </h2>
              <div className="space-y-6 text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                <p>
                  Founded with a vision to bridge the gap between traditional business operations 
                  and cutting-edge technology, Lunexa has grown from a small startup to a trusted 
                  partner for businesses worldwide.
                </p>
                <p>
                  Our journey began with a simple belief: technology should empower businesses, 
                  not complicate them. Today, we continue to innovate and deliver solutions that 
                  drive real results for our clients.
                </p>
                <p>
                  From our humble beginnings to our expansion into global marketplaces like Amazon, 
                  we've maintained our commitment to excellence and innovation.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Tilt
                tiltMaxAngleX={5}
                tiltMaxAngleY={5}
                perspective={1000}
                scale={1.02}
              >
                <div className="backdrop-blur-xl bg-white/10 dark:bg-gray-800/10 border border-white/20 dark:border-gray-700/30 rounded-3xl p-8 shadow-glass">
                  <div className="grid grid-cols-2 gap-8">
                    {stats.map((stat, index) => (
                      <motion.div
                        key={stat.label}
                        className="text-center"
                        initial={{ opacity: 0, scale: 0.5 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <motion.div
                          className={`text-4xl font-bold bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent mb-2`}
                          whileHover={{ scale: 1.1 }}
                        >
                          {stat.number}
                        </motion.div>
                        <div className="text-gray-600 dark:text-gray-400 font-medium">
                          {stat.label}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </Tilt>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-32 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              Vision & Mission
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Tilt
                tiltMaxAngleX={10}
                tiltMaxAngleY={10}
                perspective={1000}
                scale={1.02}
              >
                <div className="group h-full backdrop-blur-xl bg-white/10 dark:bg-gray-800/10 border border-white/20 dark:border-gray-700/30 rounded-3xl p-8 shadow-glass hover:shadow-glass-dark transition-all duration-500">
                  <motion.div
                    className="inline-flex p-4 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-500 mb-6 shadow-lg"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <Eye className="w-8 h-8 text-white" />
                  </motion.div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
                    Our Vision
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    To be the leading technology partner that empowers businesses to thrive in the 
                    digital age through innovative solutions and exceptional service.
                  </p>
                </div>
              </Tilt>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Tilt
                tiltMaxAngleX={10}
                tiltMaxAngleY={10}
                perspective={1000}
                scale={1.02}
              >
                <div className="group h-full backdrop-blur-xl bg-white/10 dark:bg-gray-800/10 border border-white/20 dark:border-gray-700/30 rounded-3xl p-8 shadow-glass hover:shadow-glass-dark transition-all duration-500">
                  <motion.div
                    className="inline-flex p-4 rounded-2xl bg-gradient-to-r from-green-500 to-cyan-500 mb-6 shadow-lg"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <Target className="w-8 h-8 text-white" />
                  </motion.div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
                    Our Mission
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    To deliver cutting-edge IT solutions, automation services, and AI-powered tools 
                    that drive efficiency, growth, and success for businesses of all sizes.
                  </p>
                </div>
              </Tilt>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-32 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              Our Journey
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Milestones that shaped our company
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-primary-500 to-purple-500 dark:from-cyan-400 dark:to-purple-400 rounded-full" />

            <div className="space-y-16">
              {timeline.map((item, index) => (
                <motion.div
                  key={item.year}
                  className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <Tilt
                      tiltMaxAngleX={5}
                      tiltMaxAngleY={5}
                      perspective={1000}
                      scale={1.02}
                    >
                      <div className="backdrop-blur-xl bg-white/10 dark:bg-gray-800/10 border border-white/20 dark:border-gray-700/30 rounded-2xl p-6 shadow-glass">
                        <div className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-purple-600 dark:from-cyan-400 dark:to-purple-400 bg-clip-text text-transparent mb-2">
                          {item.year}
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                          {item.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                          {item.description}
                        </p>
                      </div>
                    </Tilt>
                  </div>

                  {/* Timeline Dot */}
                  <motion.div
                    className="relative z-10 w-6 h-6 bg-gradient-to-r from-primary-500 to-purple-500 dark:from-cyan-400 dark:to-purple-400 rounded-full shadow-lg"
                    whileHover={{ scale: 1.5 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-purple-500 dark:from-cyan-400 dark:to-purple-400 rounded-full animate-ping opacity-20" />
                  </motion.div>

                  <div className="w-1/2" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-32 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600/10 via-purple-600/10 to-cyan-600/10 dark:from-primary-600/5 dark:via-purple-600/5 dark:to-cyan-600/5" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Tilt
                    tiltMaxAngleX={10}
                    tiltMaxAngleY={10}
                    perspective={1000}
                    scale={1.02}
                  >
                    <div className="group text-center h-full backdrop-blur-xl bg-white/10 dark:bg-gray-800/10 border border-white/20 dark:border-gray-700/30 rounded-3xl p-8 shadow-glass hover:shadow-glass-dark transition-all duration-500">
                      <motion.div
                        className={`inline-flex p-4 rounded-2xl bg-gradient-to-r ${value.gradient} mb-6 shadow-lg`}
                        whileHover={{ scale: 1.1, rotate: 5 }}
                      >
                        <Icon className="w-8 h-8 text-white" />
                      </motion.div>
                      <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
                        {value.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </Tilt>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;